import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllRolesViewModel } from "./AllRolesViewModel";
// import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { StyledCardContent } from "./AllRolesStyles";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import AddRole from "../manageRole/AddRole";
import { useRbac } from "../../../hooks/RbacContext";

const AllRoles = () => {
  const {
    roles,
    loading,
    error,
    handleDelete,
    createRole,
    updateRole,
    refetch,
    handleSearch,
    roleCategory,
    getRoleCategoryName,
  } = useAllRolesViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "role_name",
      label: "Name",
    },
    {
      id: "role_cat_id",
      label: "Role Category",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getRoleCategoryName(value)}
        </Typography>
      ),
      searchValue: (value) => getRoleCategoryName(value),
    },
    {
      id: "role_description",
      label: "Description",
      align: "left",
    },
    {
      id: "is_project",
      label: "Project Role",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => (value ? "yes" : "no"),
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    // {
    //   id: "is_active",
    //   label: "Status",
    //   align: "left",
    //   render: (value) => (
    //     <Chip
    //       label={value ? "Active" : "Inactive"}
    //       size="small"
    //       sx={{
    //         height: 24,
    //         fontSize: "0.75rem",
    //         fontWeight: 500,
    //         backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
    //         color: value ? "#2E7D32" : "#EF6C00",
    //         "& .MuiChip-label": {
    //           px: 1.5,
    //         },
    //       }}
    //     />
    //   ),
    //   searchValue: (value) => (value ? "active" : "inactive"),
    // },
  ];
  const fixedColumns = {
    left: [],
    right: ["is_active", "actions"],
  };
  const handleAddClient = () => {
    setSelectedRole(null);
    setIsAddRoleOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedRole(client);
    setIsAddRoleOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddRoleOpen(false);
    setSelectedRole(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedRole) {
        await updateRole(selectedRole.role_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Role updated successfully",
        });
      } else {
        await createRole(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Role added successfully",
        });
      }
      handleCloseDialog();
      await refetch();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (role) => {
    setDeleteConfirmation({
      role,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const role = deleteConfirmation.role;
    try {
      showLoading();
      await handleDelete(role);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Role deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete Role",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (client) => {
    const actions = [];

    if (checkAccess("role", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClient,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("role", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <StyledCardContent>
        <Typography color="error">Error: {error}</Typography>
      </StyledCardContent>
    );
  }
  const columnFilters = [
    {
      id: "role_cat_id",
      type: "select",
      label: "Role Category",
      render: (value) => getRoleCategoryName(value),
      icon: <BusinessIcon />,
      sendIcon: true,
    },
    {
      id: "is_project",
      type: "select",
      label: "Project Role",
      render: (value) => (value ? "Yes" : "No"),
      icon: <BusinessIcon />,
      sendIcon: true,
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={roles}
        onAdd={handleAddClient}
        showActions={true}
        showAddButton={checkAccess("role", "add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Roles"
        subtitle="Manage and monitor all roles"
        addButtonText="Add Roles"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddRole
        open={isAddRoleOpen}
        onClose={handleCloseDialog}
        initialData={selectedRole}
        onSubmit={handleSubmit}
        roleCategory={roleCategory}
        getRoleCategoryName={getRoleCategoryName}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Role"
          message={`Are you sure you want to delete role "${deleteConfirmation.role.role_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllRoles;
